import { Client, ClientFormData } from "@/types/client";
import { PageRes, apiRequest } from "./api";

const CLIENT_URL = "/app/clients";

function normalizeClient(raw: Record<string, unknown>): Client {
  const status = raw.status as string | undefined;
  const isActive = status === "active";
  return {
    ...(raw as unknown as Client),
    isActive,
    status,
    tags: Array.isArray(raw.tags) ? (raw.tags as string[]) : [],
  };
}

const clientService = {
  async getAll(
    search?: string,
    page?: number,
    limit?: number,
    tags?: string[],
    companyId?: string,
    projectId?: string
  ): Promise<[PageRes<Client> | null, any]> {
    const params: Record<string, string | number> = {};
    if (search) params.search = search;
    if (page) params.page = page;
    if (limit) params.limit = limit;
    if (tags?.length) params.tags = tags.join(",");
    if (companyId) params.companyId = companyId;
    if (projectId) params.projectId = projectId;

    const [data, error] = await apiRequest<PageRes<Record<string, unknown>>>({
      loaderKey: "clients",
      method: "get",
      url: CLIENT_URL,
      params,
    });
    if (error || !data) return [null, error];
    const result = (data.result || []).map((r) => normalizeClient(r));
    return [{ ...data, result } as PageRes<Client>, null];
  },

  async getById(id: string): Promise<[Client | null, any]> {
    const [data, error] = await apiRequest<Record<string, unknown>>({
      loaderKey: "client",
      method: "get",
      url: `${CLIENT_URL}/${id}`,
    });
    if (error || !data) return [null, error];
    return [normalizeClient(data), null];
  },

  async create(clientData: ClientFormData): Promise<[any, any]> {
    const { name, emails, password, phones, isActive, tags, companyMemberships } = clientData;
    return apiRequest({
      loaderKey: "createClient",
      method: "post",
      url: CLIENT_URL,
      data: {
        name,
        emails,
        password,
        phones,
        isActive,
        tags,
        companyMemberships,
      },
    });
  },

  async update(id: string, clientData: Partial<ClientFormData>): Promise<[any, any]> {
    const { name, emails, phones, isActive, tags, companyMemberships } = clientData;
    return apiRequest({
      loaderKey: "updateClient",
      method: "put",
      url: `${CLIENT_URL}/${id}`,
      data: { name, emails, phones, isActive, tags, companyMemberships },
    });
  },

  /** Mescla `mergedId` dentro de `survivorId`: combina empresas/projetos/tags, reatribui tickets/conversas/chamadas e apaga o mesclado. */
  async merge(survivorId: string, mergedId: string): Promise<[Client | null, any]> {
    const [data, error] = await apiRequest<{ result: Record<string, unknown> }>({
      loaderKey: "mergeClient",
      method: "post",
      url: `${CLIENT_URL}/${survivorId}/merge`,
      data: { mergedId },
    });
    if (error || !data?.result) return [null, error];
    return [normalizeClient(data.result), null];
  },

  /** Loga o agente direto como este contato, sem a senha dele — só pra debug/suporte (ver a
   * tela exatamente como o cliente vê). */
  async impersonate(
    id: string
  ): Promise<[{ accessToken: string; user: Record<string, unknown>; subdomain?: string } | null, any]> {
    return apiRequest<{ accessToken: string; user: Record<string, unknown>; subdomain?: string }>({
      loaderKey: "impersonateClient",
      method: "post",
      url: `/app/client-auth/impersonate/${id}`,
    });
  },
};

export default clientService;
